import { Location } from 'history'
import LoadingView from 'components/Loading'
import TopBar from 'components/TopBar'
import { BROWSER, PLATFORM } from 'core/constants'
import routes, { GALLERY_BOUND_ROUTES } from 'core/modules/router'
import MyCollection from 'pages/Authenticated/MyCollection'
import CollectionItemDetail from 'pages/Authenticated/MyCollection/CollectionItemDetail'
import OpenDrop from 'pages/Authenticated/OpenDrop'
import OpeningDrop from 'pages/Authenticated/OpeningDrop'
import Payment from 'pages/Authenticated/Payment/V2'
import PendingPayment from 'pages/Authenticated/PendingPayment'
import UserProfile from 'pages/Authenticated/UserProfile'
import ConnectView from 'pages/Unauthenticated/Connect'
import Details from 'pages/Unauthenticated/Drops/Details'
import ListView from 'pages/Unauthenticated/Drops/List/'
// Tenant oken: AssetList import removed (not used)
import AssetDetail from 'pages/Unauthenticated/Asset/AssetDetail'
import Terms from 'pages/Unauthenticated/Terms'
import FaqView from 'pages/Unauthenticated/FAQ'
import TransferTokensFlow from 'components/TransferTokensFlow/TransferTokensFlow.logic'
import React, {
  Suspense,
  lazy,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import {
  Route,
  Switch,
  useLocation,
  matchPath,
  useRouteMatch,
} from 'react-router-dom'
import PrivateRoute from './GuardedRoute/GuardedRoute.view'
import { useStorePreviousLocation } from './MainRouter.hook'
import MarketplaceLogic from 'pages/Unauthenticated/Marketplace/Marketplace.logic'
import BuyOffer from 'pages/Authenticated/Marketplace/BuyOffer'
import CreateOffer from 'pages/Authenticated/Marketplace/CreateOffer'
import { useFeatures } from 'context/Feature'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import useMainRouterStyles from './MainRouter.style'
import { TransitionAnimationTypes } from '@onepercentio/one-ui/components/Transition'
import GalleryDetails from 'openspace/pages/Unauthenticated/GalleryDetails'
import useLanguage from 'openspace/hooks/useLanguage'
import useCustomScrollBar from '@onepercentio/one-ui/hooks/ui/useCustomScrollbar'
import SCSSStyles from './MainRouter.module.scss'
import { debounce } from 'lodash'
import ROUTES from 'core/modules/router'
import AllGalleriesLogic from 'openspace/pages/Unauthenticated/AllGalleries'

const LazyRouteComp = lazy(() =>
  import('./AsyncRoutes/AsyncRoutes').then((r) => ({
    default: ({ route }: { route: keyof typeof r }) => {
      const Comp = r[route]
      return <Comp />
    },
  }))
)

export interface AppViewProps {
  /**
   * Show the block loading app view
   */
  isLoading: boolean
  /**
   * Determines wheter a root level transition should be animated
   */
  animateRoot: (location: Location) => number
}

export interface MainRouterViewProps {
  /**
   * Determines wheter a root level transition should be animated
   */
  animateRoot: (location: Location) => number
}

const AppView: React.FC<AppViewProps> = ({ isLoading, animateRoot }) => {
  return (
    <Suspense fallback={<LoadingView />}>
      <MainRouterView animateRoot={animateRoot} />
    </Suspense>
  )
}

const goToTop = () => window.scrollTo(0, 0)

const ROUTES_WITHOUT_TOPBAR = [
  routes.authenticated.transferTokens,
  '/user',
  routes.unauthenticated.terms,
  routes.unauthenticated.regulation,
  routes.unauthenticated.signIn,
  routes.unauthenticated.signUp,
  routes.authenticated.waitingRoom,
  routes.authenticated.checkout,
  routes.authenticated.payment,
  routes.authenticated.pendingPayment,
  routes.authenticated.open,
  routes.authenticated.opening,
  routes.unauthenticated.claimSignup,
  routes.unauthenticated.claim,
]

const MainRouterView: React.FC<MainRouterViewProps> = ({ animateRoot }) => {
  const lng = useLanguage()
  const location = useLocation<Location>()
  const pathPattern = matchPath(location.pathname, [
    ROUTES.unauthenticated.creators,
  ])?.path
  const pathKey = pathPattern || location.pathname
  const salt = useMemo(() => pathKey + Math.random() + lng, [pathKey, lng])

  // Persists the previous location on Local Storage.
  useStorePreviousLocation()

  const { elRef } = useCustomScrollBar({
    propagate: PLATFORM === 'windows' || PLATFORM === 'linux',
  })

  useInsertionEffect(() => {
    ; (elRef as any).current = document.body
  }, [])

  const isMarketplaceEnabled = useFeatures().marketplace
  const classes = useMainRouterStyles()

  const shouldIncludePadding = !useRouteMatch(ROUTES_WITHOUT_TOPBAR)?.isExact

  return (
    <>
      <Switch location={location}>
        <Route path={ROUTES_WITHOUT_TOPBAR}>{null}</Route>
        <Route path='/'>
          <TopBar />
        </Route>
      </Switch>
      <UncontrolledTransition
        id='scrollable'
        className={`${classes.container} ${BROWSER}`}
        transitionType={TransitionAnimationTypes.FADE}
        contentClassName={`${classes.content} ${!shouldIncludePadding ? classes.noSpacing : ''
          }`}>
        <RoutesWrapper key={salt} location={location} />
      </UncontrolledTransition>
    </>
  )
}

function RoutesWrapper({
  location,
}: {
  location: ReturnType<typeof useLocation>
}) {
  const scrollableRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = scrollableRef.current!.parentElement!
    const recover = debounce(() => {
      el.classList.remove(SCSSStyles.blockHover)
    }, 600)
    const cb = () => {
      el.classList.add(SCSSStyles.blockHover)
      recover()
    }
    el.addEventListener('scroll', cb, {
      passive: true,
    })
    return () => {
      el.removeEventListener('scroll', cb)
    }
  }, [])

  return (
    <div ref={scrollableRef}>
      <RouterMapping location={location} />
    </div>
  )
}

export function RouterMapping({
  location,
}: {
  location: ReturnType<typeof useLocation>
}) {
  const nodeRef = useRef<HTMLDivElement>(null)
  const isMarketplaceEnabled = useFeatures().marketplace

  return (
    <>
      <Switch location={location}>
        <PrivateRoute path={routes.authenticated.transferTokens}>
          <TransferTokensFlow />
        </PrivateRoute>

        <Route path={routes.unauthenticated.faq}>
          <FaqView />
        </Route>
        <Route path={routes.unauthenticated.terms}>
          <Terms termsType='terms' />
        </Route>
        <Route path={routes.unauthenticated.risk}>
          <Terms termsType='risk' />
        </Route>
        <Route path={routes.unauthenticated.signIn}>
          <ConnectView />
        </Route>
        <Route path={routes.unauthenticated.signUp}>
          <ConnectView />
        </Route>
        <PrivateRoute path={routes.authenticated.checkout}>
          <LazyRouteComp route='Checkout' />
        </PrivateRoute>
        <PrivateRoute path={routes.authenticated.payment}>
          <Payment />
        </PrivateRoute>
        <PrivateRoute
          path={[
            routes.authenticated.pendingPayment,
            GALLERY_BOUND_ROUTES.authenticated.pendingPayment,
          ]}>
          <PendingPayment />
        </PrivateRoute>
        <PrivateRoute path={routes.authenticated.open}>
          <OpenDrop />
        </PrivateRoute>
        <PrivateRoute path={routes.authenticated.opening}>
          <OpeningDrop />
        </PrivateRoute>
        <Route path='/'>
          <Switch key={location.pathname}>
            <Route exact path={'/'}>
              <ListView />
            </Route>

            <div ref={nodeRef} className='fullHeight scroll-root'>
              <Switch location={location}>
                <Route key='root' exact path={ROUTES.unauthenticated.allGalleries}>
                  <AllGalleriesLogic />
                </Route>,
                <PrivateRoute
                  path={GALLERY_BOUND_ROUTES.authenticated.editOffer}>
                  <CreateOffer />
                </PrivateRoute>
                <PrivateRoute
                  path={[
                    GALLERY_BOUND_ROUTES.authenticated.offerItemDetail,
                    routes.authenticated.offerItemDetail,
                  ]}>
                  <CollectionItemDetail />
                </PrivateRoute>
                <Route path={routes.unauthenticated.dropDetails}>
                  <Details />
                </Route>
                <PrivateRoute path={routes.authenticated.userAccount} exact>
                  <UserProfile />
                </PrivateRoute>
                <PrivateRoute exact path={routes.authenticated.myCollection}>
                  <MyCollection />
                </PrivateRoute>
                <PrivateRoute
                  exact
                  path={
                    GALLERY_BOUND_ROUTES.authenticated.marketplaceOfferBuy
                  }>
                  <BuyOffer />
                </PrivateRoute>
                <PrivateRoute
                  path={
                    GALLERY_BOUND_ROUTES.authenticated.collectionItemDetail
                  }>
                  <CollectionItemDetail />
                </PrivateRoute>
                <PrivateRoute
                  path={GALLERY_BOUND_ROUTES.authenticated.sellMarketplace}>
                  <CreateOffer />
                </PrivateRoute>
                <Route
                  path={[
                    routes.unauthenticated.assetDetail,
                    GALLERY_BOUND_ROUTES.unauthenticated.assetDetail,
                  ]}>
                  <AssetDetail marketplace={false} />
                </Route>
                <Route
                  path={[
                    GALLERY_BOUND_ROUTES.unauthenticated
                      .marketplaceAssetDetail,
                    routes.unauthenticated.marketplaceAssetDetail,
                  ]}>
                  <AssetDetail marketplace={isMarketplaceEnabled} />
                </Route>
                {/* Tenant oken: Asset list route removed */}
                <Route exact path={routes.unauthenticated.galleryDetail}>
                  <GalleryDetails />
                </Route>
                <Route
                  exact
                  path={[
                    GALLERY_BOUND_ROUTES.unauthenticated.marketplace,
                    ROUTES.unauthenticated.marketplace,
                  ]}>
                  <MarketplaceLogic />
                </Route>
              </Switch>
            </div>
          </Switch>
        </Route>
      </Switch></>
  )
}

export default AppView
